import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Download, Smartphone } from 'lucide-react';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const supportsDirectInstall = Boolean(deferredPrompt);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if dismissed recently (within 7 days)
    const dismissedTime = localStorage.getItem('pwaPromptDismissed');
    if (dismissedTime && Date.now() - parseInt(dismissedTime) < 7 * 24 * 60 * 60 * 1000) {
      return;
    }

    // Listen for the beforeinstallprompt event (Android/Desktop Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show immediately for better visibility
      setShowPrompt(true);
    };

    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // For iOS, show manual instructions after short delay
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 2000);
    }
    
    // For mobile browsers that support PWA but may not fire beforeinstallprompt
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile && !standalone && !iOS) {
      setTimeout(() => {
        if (!deferredPrompt) {
          setShowPrompt(true);
        }
      }, 2000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };

  // Don't show if already installed or shouldn't show
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[10001] p-4 md:p-6 animate-slide-up pointer-events-auto" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }} data-testid="pwa-install-prompt">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-2xl border border-stone-200 overflow-hidden pointer-events-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#5C0120] to-[#5C0120]/90 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Smartphone className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Install Our App</h3>
                <p className="text-xs text-white/80">Add to your home screen</p>
              </div>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-white/80 hover:text-white p-1 pointer-events-auto"
              aria-label="Dismiss"
              data-testid="pwa-dismiss-button"
              style={{ touchAction: 'manipulation' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-stone-600 mb-4">
            Get quick access to Gibraltar Wedding Expo. Save vendors, plan your wedding, and get updates - all from your home screen!
          </p>
          
          {isIOS ? (
            // iOS Instructions
            <div className="space-y-3">
              <p className="text-sm font-medium text-stone-700">To install on iPhone/iPad:</p>
              <ol className="text-sm text-stone-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="bg-[#D4AF37] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <span>Tap the <strong>Share</strong> button in Safari</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-[#D4AF37] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-[#D4AF37] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                  <span>Tap <strong>"Add"</strong> to confirm</span>
                </li>
              </ol>
              <Button 
                onClick={handleDismiss}
                className="w-full mt-2 bg-stone-100 text-stone-700 hover:bg-stone-200"
                data-testid="pwa-ios-dismiss-button"
              >
                Got it!
              </Button>
            </div>
          ) : supportsDirectInstall ? (
            <Button 
              onClick={handleInstall}
              className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white pointer-events-auto"
              data-testid="pwa-install-button"
              style={{ touchAction: 'manipulation' }}
            >
              <Download className="h-4 w-4 mr-2" />
              Install App
            </Button>
          ) : (
            <div className="space-y-3" data-testid="pwa-manual-install-instructions">
              <p className="text-sm font-medium text-stone-700">Install from your browser menu:</p>
              <ol className="text-sm text-stone-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="bg-[#D4AF37] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <span>Open the browser menu in the top or bottom toolbar.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-[#D4AF37] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <span>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-[#D4AF37] text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                  <span>Confirm the install prompt from your browser.</span>
                </li>
              </ol>
              <Button 
                onClick={handleDismiss}
                className="w-full bg-stone-100 text-stone-700 hover:bg-stone-200 pointer-events-auto"
                data-testid="pwa-manual-dismiss-button"
                style={{ touchAction: 'manipulation' }}
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
